import { Icon, type IconName } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatPercentFromBps, shortenName } from '../../../lib/format';
import { descreverDuracao, type EstimativaEnvio } from '../estimativa';
import type { PublicoCalculado } from '../formulario';
import type { SellerOption, SenderKind, WhatsAppTariffs } from '../types';

/**
 * Estimativa ANTES de disparar: o que custa (exato) e o que deve voltar
 * (pelo historico da empresa), em termos de negocio.
 *
 * Decisoes nao obvias:
 *  1. Custo e retorno ficam lado a lado com o "custo por recuperacao" e a
 *     "comissao por R$ 1 de custo": e a pergunta que a administracao faz
 *     antes de apertar o botao.
 *  2. Sem historico, o bloco de retorno vira um estado vazio que ENSINA
 *     (por que nao ha numero e quando aparece) em vez de inventar taxa.
 *  3. Respostas esperadas dizem PARA ONDE vao (as vendedoras marcadas ou o
 *     rodizio): e o trabalho que o envio cria para o comercial.
 */

function Numero({ icone, rotulo, valor, apoio }: { readonly icone: IconName; readonly rotulo: string; readonly valor: string; readonly apoio: string }): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 rounded-lg border border-line bg-elevated px-3 py-2.5">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">
        <Icon name={icone} size="xs" />
        {rotulo}
      </p>
      <p className="break-words text-lg font-semibold leading-tight tabular-nums text-ink">{valor}</p>
      <p className="text-xs text-ink-subtle">{apoio}</p>
    </div>
  );
}

export function EstimativaDoEnvio({
  estimativa,
  publico,
  senderKind,
  tariffs,
  feeBps,
  sellers,
  vendedorasEscolhidas,
  janelaInicio,
  janelaFim,
  viaCsv,
}: {
  readonly estimativa: EstimativaEnvio;
  readonly publico: PublicoCalculado;
  readonly senderKind: SenderKind | null;
  readonly tariffs: WhatsAppTariffs;
  readonly feeBps: number;
  readonly sellers: readonly SellerOption[];
  readonly vendedorasEscolhidas: readonly string[];
  readonly janelaInicio: string;
  readonly janelaFim: string;
  readonly viaCsv: boolean;
}): JSX.Element {
  const r = estimativa.retorno;
  const destino = vendedorasEscolhidas.length === 0 ? (sellers.length === 0 ? 'o número do comercial, sem vendedora' : 'o rodízio das vendedoras') : sellers.filter((s) => vendedorasEscolhidas.includes(s.id)).map((s) => shortenName(s.name)).join(', ');
  const tarifaTexto =
    senderKind === null
      ? 'escolha o número de saída'
      : senderKind === 'evolution'
        ? `sem tarifa por mensagem (instância mensal de ${formatBRLFromCents(tariffs.evolutionMonthlyCents)} já paga)`
        : `${formatBRLFromCents(estimativa.tarifaCents)} por conversa na Meta`;
  const comissaoPorReal = r === null || estimativa.custoCents <= 0 ? null : Math.round((r.comissaoCents * 100) / estimativa.custoCents);

  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-1 2xl:grid-cols-3">
        <Numero
          icone="leads"
          rotulo="Contatos"
          valor={formatCount(estimativa.contatos)}
          apoio={publico.recentes > 0 ? `${formatCount(publico.bruto)} no público, menos ${formatCount(publico.recentes)} contatados há pouco` : 'repetidos e bloqueados saem na hora'}
        />
        <Numero icone="moeda" rotulo="Custo" valor={formatBRLFromCents(estimativa.custoCents)} apoio={tarifaTexto} />
        <Numero icone="relogio" rotulo="Duração" valor={estimativa.duracao === null ? '—' : descreverDuracao(estimativa.duracao, janelaInicio, janelaFim)} apoio="no ritmo e na janela escolhidos" />
      </div>

      {r === null ? (
        <div className="rounded-lg border border-dashed border-edge bg-elevated px-3 py-3 text-sm">
          <p className="font-medium text-ink">Retorno esperado: ainda sem base</p>
          <p className="mt-0.5 text-ink-muted">
            {estimativa.contatos === 0
              ? 'Marque um público para ver quanto ele costuma trazer.'
              : viaCsv
                ? 'Lista importada não tem histórico no motor. O custo acima é exato; o retorno aparece no relatório, conforme os pagamentos entrarem.'
                : 'Nenhum envio anterior para este público nesta empresa. O primeiro envio cria a base; a partir dele, a estimativa aparece aqui.'}
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-status-good/40 bg-status-good/5 px-3 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Retorno esperado</p>
          <p className="mt-1 text-2xl font-semibold tracking-tight text-ink">{formatBRLFromCents(r.recuperadoCents)}</p>
          <p className="text-sm text-ink-muted">
            cerca de {formatCount(r.pagamentos)} {r.pagamentos === 1 ? 'pagamento' : 'pagamentos'} em até 7 dias · comissão de {formatBRLFromCents(r.comissaoCents)} ({formatPercentFromBps(feeBps)})
          </p>
          <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <dt className="text-ink-subtle">Custo por recuperação</dt>
            <dd className="text-right tabular-nums text-ink">{estimativa.custoPorRecuperacaoCents === null ? '—' : formatBRLFromCents(estimativa.custoPorRecuperacaoCents)}</dd>
            <dt className="text-ink-subtle">Comissão por R$ 1 de custo</dt>
            <dd className="text-right tabular-nums text-ink">{comissaoPorReal === null ? 'sem custo' : formatBRLFromCents(comissaoPorReal)}</dd>
            <dt className="text-ink-subtle">Respostas para o Comercial</dt>
            <dd className="text-right tabular-nums text-ink">≈ {formatCount(r.respostas)}</dd>
          </dl>
          <p className="mt-2 text-xs text-ink-subtle">
            As respostas vão para {destino}. Base: {formatCount(r.baseEnvios)} {r.baseEnvios === 1 ? 'envio anterior' : 'envios anteriores'} ({formatCount(r.baseMensagens)} mensagens) para estes
            públicos nesta empresa{r.parcial ? '; parte do público ainda não tem histórico' : ''}. É expectativa, não promessa: a comissão real é a do ADR-001 (último contato, 7 dias).
          </p>
        </div>
      )}
    </div>
  );
}
